import { useMemo, useState } from "react";
import { tokensIn } from "../../../shared/personalize";
import { validateDemo } from "../../../shared/validateDemo";
import type { PublishIssue } from "../../../shared/types";
import { api, ApiError } from "../../api";
import { TabFooter, useEditor } from "../DemoEditor";

export default function PublishTab() {
  const { bundle, setBundle } = useEditor();
  const [serverIssues, setServerIssues] = useState<PublishIssue[] | null>(null);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [src, setSrc] = useState("");
  const [copied, setCopied] = useState<string | null>(null);
  const issues = useMemo(() => serverIssues ?? validateDemo(bundle), [bundle, serverIssues]);
  const errors = issues.filter((i) => i.level === "error");
  const warnings = issues.filter((i) => i.level === "warning");
  const live = bundle.demo.status === "published";

  const url = `${location.origin}/${bundle.product.slug}/${bundle.demo.slug}`;
  // Variables this demo actually uses, so the link builder offers exactly those.
  const used = useMemo(() => {
    const d = bundle.demo;
    const texts = [d.surveyIntro, d.ctaLabel, d.ctaUrl, d.leadTitle,
      ...bundle.questions.flatMap((q) => [q.prompt, ...q.options.map((o) => o.label)]),
      ...bundle.steps.flatMap((st) => [st.caption, ...st.hotspots.flatMap((h) => [h.tooltipText, h.linkUrl])])];
    const keys = [...new Set(texts.flatMap((x) => tokensIn(x).map((k) => k.key)))];
    return keys.length ? keys : ["company", "first_name"];
  }, [bundle]);
  const [vals, setVals] = useState<Record<string, string>>({});
  const tagged = useMemo(() => {
    const q = new URLSearchParams();
    if (src.trim()) q.set("src", src.trim().toLowerCase().replace(/\s+/g, "-"));
    for (const k of used) if (vals[k]?.trim()) q.set(k, vals[k].trim());
    const qs = q.toString();
    return qs ? `${url}?${qs}` : url;
  }, [url, src, used, vals]);

  const copy = async (text: string) => {
    await navigator.clipboard.writeText(text);
    setCopied(text);
    setTimeout(() => setCopied(null), 1500);
  };
  const publish = async () => {
    setMsg(null);
    try {
      setBundle(await api.publish(bundle.demo.id));
      setServerIssues(null);
      setMsg({ ok: true, text: "Published. The link below is live." });
    } catch (e) {
      if (e instanceof ApiError && e.issues) setServerIssues(e.issues);
      setMsg({ ok: false, text: (e as Error).message });
    }
  };
  const unpublish = async () => {
    if (!confirm("Unpublish? The public link stops working until you publish again.")) return;
    setBundle(await api.unpublish(bundle.demo.id));
    setMsg({ ok: true, text: "Unpublished. The demo is a draft again." });
  };

  return (
    <div className="stack" style={{ maxWidth: 820 }}>
      <section className="panel stack">
        <h2>Pre-publish check</h2>
        {issues.length === 0 && <div className="ok-box">Everything checks out.</div>}
        {errors.length > 0 && (
          <div className="stack-s">
            <b className="text-danger">Must fix before publishing</b>
            {errors.map((i) => <div key={i.message} className="error-box">{i.message}</div>)}
          </div>
        )}
        {warnings.length > 0 && (
          <div className="stack-s">
            <b className="text-warn">Worth a look</b>
            {warnings.map((i) => <div key={i.message} className="route-note" style={{ margin: 0 }}>{i.message}</div>)}
          </div>
        )}
        {msg && <div className={msg.ok ? "ok-box" : "error-box"}>{msg.text}</div>}
        <div className="row">
          {live ? (
            <>
              <button className="btn btn-primary" onClick={publish} disabled={errors.length > 0}>Republish</button>
              <button className="btn btn-danger" onClick={unpublish}>Unpublish</button>
              <span className="small muted">Edits to a live demo show up immediately. Republish re-runs the checks.</span>
            </>
          ) : (
            <button className="btn btn-primary" onClick={publish} disabled={errors.length > 0}>Publish demo</button>
          )}
        </div>
      </section>

      {live && (
        <section className="panel stack">
          <h2>Share</h2>
          <div className="row">
            <input type="text" readOnly value={url} onFocus={(e) => e.target.select()} style={{ flex: 1 }} />
            <button className="btn" onClick={() => copy(url)}>{copied === url ? "Copied" : "Copy link"}</button>
            <a className="btn" href={url} target="_blank" rel="noreferrer">Open</a>
          </div>
          <label className="field">
            <span>Personalised link for a prospect or campaign <span className="hint">The tag shows under "Sources" in analytics; values fill {"{{variables}}"}</span></span>
            <div className="link-builder">
              <label className="field"><span className="hint">Source tag</span>
                <input type="text" placeholder="e.g. acme-shipping" value={src} onChange={(e) => setSrc(e.target.value)} />
              </label>
              {used.map((k) => (
                <label key={k} className="field"><span className="hint mono">{`{{${k}}}`}</span>
                  <input type="text" value={vals[k] ?? ""} onChange={(e) => setVals({ ...vals, [k]: e.target.value })} />
                </label>
              ))}
            </div>
            <div className="row">
              <input type="text" readOnly value={tagged} style={{ flex: 1 }} />
              <button className="btn" onClick={() => copy(tagged)} disabled={tagged === url}>{copied === tagged ? "Copied" : "Copy"}</button>
            </div>
          </label>
          <label className="field">
            <span>Embed on a website</span>
            <input type="text" readOnly onFocus={(e) => e.target.select()}
              value={`<iframe src="${url}" style="width:100%;aspect-ratio:16/10;border:0" allowfullscreen></iframe>`} />
          </label>
        </section>
      )}
      <TabFooter back="preview" next={live ? "analytics" : undefined} nextLabel="View analytics" />
    </div>
  );
}
